import ErrorBoundary from 'Components/Core/ErrorBoundary';
import { NamePrefixModifier } from 'Components/Core/InjectedPropsContext';
import { actions as appActions } from 'Data/ReduxViewModels/App';
import { TitleSource } from 'Data/ReduxViewModels/App/types';
import { hasNonEmptyValue } from 'Lib/Any';
import { pipe } from 'Lib/Hooks/Selectors';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { actions } from '../../../Data/ReduxViewModels/WorksheetEditor';
import { Message, Spinner } from '../../Core';
import PageSection from '../../Layout/PageSection';
import { getWorksheetComponent } from '../../Worksheets';
import {
  WorksheetEditorInjectedPropsProvider,
  WorksheetSnapshotPreviewInjectedPropsProvider,
} from '../../Worksheets/Core/WorksheetEditorInjectedPropsProvider';
import { FixedWorksheetHeader, WorksheetHeader } from '../../Worksheets/Core/WorksheetHeader';
import { FixedSnapshotHeader } from '../../Worksheets/Core/WorksheetSnapshots';
import Page from '../Core/Page';
import './WorksheetPage.scss';

const enableSnapshotPrototype = process.env.REACT_APP_ENABLE_SNAPSHOTS_PROTOTYPE === 'true';

let WorksheetPage: React.FC = () => {
  const params = useParams();
  const worksheetId = params.worksheetId?.toLowerCase();
  const snapshotIdParam = params.snapshotId;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const worksheetType = useSelector((s) => s.worksheetEditor.worksheetType);
  const worksheetLoaded = useSelector((s) => hasNonEmptyValue(s.worksheetEditor.syncModel));
  const snapshotId = useSelector((s) => s.worksheetEditor.snapshotPreview?.id);
  const ready = useSelector((s) => s.worksheetEditor.status?.ready);
  const rootDataPath = 'data';

  const deleted = useSelector((state) => state.worksheetEditor.status.deleted);
  const deleting = useSelector((state) => state.worksheetEditor.status.deleting);

  useEffect(() => {
    if (deleted) {
      navigate('/');
      dispatch(actions.clearDeleteWorksheet());
    }
  }, [navigate, dispatch, deleted]);

  const WorksheetTypeId = worksheetType && worksheetType.Id;
  const WorksheetComponent = getWorksheetComponent(WorksheetTypeId);

  const InjectedPropsProvider =
    enableSnapshotPrototype && snapshotId
      ? WorksheetSnapshotPreviewInjectedPropsProvider
      : WorksheetEditorInjectedPropsProvider;

  useEffect(() => {
    dispatch(actions.init({ worksheetId }));
    return () => {
      dispatch(actions.unready());
    };
  }, [dispatch, worksheetId, location]);

  useEffect(() => {
    dispatch(actions.setSnapshotPreviewId({ id: snapshotIdParam }));
  }, [dispatch, snapshotIdParam]);

  const pageTitle = useSelector(
    pipe(
      (state) => (state.worksheetEditor.syncModel as any)?.header?.Sample?.SampleId,
      (sampleId) =>
        worksheetLoaded && ready ? [sampleId, worksheetType.Caption].filter(hasNonEmptyValue).join(' - ') : undefined
    )
  );
  useEffect(() => {
    if (hasNonEmptyValue(pageTitle)) dispatch(appActions.setPageTitle({ pageTitle, source: TitleSource.Specific }));
    return () => {
      dispatch(appActions.setPageTitle({ pageTitle: undefined, source: TitleSource.Specific }));
    };
  }, [dispatch, pageTitle]);

  if (!ready || deleting) {
    return (
      <Page>
        <Spinner large />
      </Page>
    );
  } else if (!WorksheetComponent) {
    return (
      <Page>
        <PageSection>
          <Message error>
            The worksheet component could not be found, please check that the ID is correct and the worksheet type is
            configured correctly.
          </Message>
        </PageSection>
      </Page>
    );
  } else if (!worksheetLoaded) {
    return (
      <Page>
        <PageSection>
          <Message warning>No worksheet loaded.</Message>
        </PageSection>
      </Page>
    );
  } else {
    return (
      <Page>
        <div className="WorksheetPage">
          <ErrorBoundary>
            <InjectedPropsProvider>
              <NamePrefixModifier namePrefix={rootDataPath}>
                {enableSnapshotPrototype && <FixedSnapshotHeader />}
                <FixedWorksheetHeader />
                <div className="WorksheetPage-Content">
                  <WorksheetHeader />
                  <WorksheetComponent />
                </div>
              </NamePrefixModifier>
            </InjectedPropsProvider>
          </ErrorBoundary>
        </div>
      </Page>
    );
  }
};

export default WorksheetPage;
